import React, { useState } from 'react';
import Layout from 'layouts/manager/Layout';
import Pagination from 'components/Pagination';
import Table, { TableColumn } from 'components/Table';

const tableData = [
  {
    loginId: 'user001',
    company: '株式会社A',
    name: '山田 太郎',
    email: 'yamada@example.com',
    status: 'active',
  },
  {
    loginId: 'user002',
    company: '株式会社B',
    name: '佐藤 花子',
    email: 'sato@example.com',
    status: 'inactive',
  },
  {
    loginId: 'user003',
    company: '株式会社C',
    name: '鈴木 次郎',
    email: 'suzuki@example.com',
    status: 'active',
  },
];

type TableRow = {
  loginId: string;
  company: string;
  name: string;
  email: string;
  status: string;
};

const actionLinks = [
  { label: '編集', color: 'text-green-700', hover: 'hover:underline', href: (row: TableRow) => `/manager/login-id/edit/${row.loginId}` },
  { label: '店舗管理', color: 'text-blue-700', hover: 'hover:underline', href: (row: TableRow) => `/manager/login-id/${row.loginId}/member-link` },
  { label: '権限設定', color: 'text-indigo-700', hover: 'hover:underline', href: (row: TableRow) => `/manager/login-id/edit/${row.loginId}` },
  { label: '協力パートナー管理', color: 'text-teal-700', hover: 'hover:underline', href: (row: TableRow) => `/manager/login-id/edit/${row.loginId}` },
  { label: '削除', color: 'text-red-600', hover: 'hover:underline', href: (row: TableRow) => `/manager/login-id/delete/${row.loginId}` },
];

type SortKey = 'loginId' | 'company' | 'name' | 'email' | 'status';
type SortOrder = 'asc' | 'desc';

const Search: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [checked, setChecked] = useState<number[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('loginId');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [search, setSearch] = useState({
    loginId: '',
    company: '',
    name: '',
    email: '',
  });
  // ページネーション用
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const totalRows = tableData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const pagedData = [...tableData].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  }).slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // チェックボックス
  const handleCheck = (idx: number) => {
    setChecked((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };
  const handleCheckAll = () => {
    if (checked.length === tableData.length) {
      setChecked([]);
    } else {
      setChecked(tableData.map((_, idx) => idx));
    }
  };

  // ソートリンク
  const renderSortLink = (key: SortKey, label: string) => (
    <button
      type="button"
      className="flex items-center text-xs font-medium text-gray-700 hover:underline focus:outline-none"
      onClick={() => {
        if (sortKey === key) {
          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setSortKey(key);
          setSortOrder('asc');
        }
      }}
    >
      {label}
      {sortKey === key && (
        <span className="ml-1 text-xs">{sortOrder === 'asc' ? '▲' : '▼'}</span>
      )}
    </button>
  );

  const columns: TableColumn<TableRow>[] = [
    { key: 'loginId', label: 'ログインID' },
    { key: 'company', label: '会社名' },
    { key: 'name', label: '担当者名' },
    { key: 'email', label: 'メールアドレス' },
    { key: 'status', label: 'ステータス', render: (row) => row.status === 'active' ? '有効' : '無効' },
  ];

  return (
    <Layout>
      <div className="mx-auto">
        {/* タイトル */}
        <h1 className="mb-3 text-lg font-bold text-gray-800">ログインID管理</h1>
        {/* 検索項目 */}
        <div className="mb-6">
          <button
            className="flex items-center w-full px-4 py-2 rounded-t-lg"
            style={{ backgroundColor: '#f7fafd' }}
            onClick={() => setOpen((v) => !v)}
            type="button"
          >
            <span
              className="mr-2 text-lg transition-transform"
              style={{ color: '#002c6a', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
            >
              ▼
            </span>
            <span className="flex-1 text-base font-bold text-left" style={{ color: '#002c6a' }}>
              検索
            </span>
          </button>
          {open && (
            <div className="p-4 bg-white border rounded-b-lg shadow">
              <form className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="block mb-1 text-sm text-gray-600">ログインID</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.loginId} onChange={e => setSearch({ ...search, loginId: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">会社名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.company} onChange={e => setSearch({ ...search, company: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">担当者名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.name} onChange={e => setSearch({ ...search, name: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">メールアドレス</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" value={search.email} onChange={e => setSearch({ ...search, email: e.target.value })} />
                </div>
                <div className="flex justify-start mt-2 md:col-span-3">
                  <button
                    type="submit"
                    className="px-3 py-1 text-sm transition rounded"
                    style={{ color: '#ffffff', background: '#002c6a' }}
                  >
                    検索
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
        {/* 一覧テーブル */}
        <div className="p-4 bg-white border rounded shadow">
          <div className="flex justify-start mb-2">
            <button
              className="px-3 py-1 text-xs font-semibold rounded"
              style={{ background: '#002c6a', color: '#fff' }}
              type="button"
              onClick={() => { window.location.href = '/manager/login-id/add'; }}
            >
              ログインID追加
            </button>
          </div>
          <Table
            data={pagedData}
            columns={columns}
            checked={checked}
            onCheck={handleCheck}
            onCheckAll={handleCheckAll}
            showCheckbox
            actions={actionLinks}
            renderSortLink={renderSortLink}
          />
          {/* ページネーション */}
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>
    </Layout>
  );
};

export default Search;
